import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Tooltip } from '@neo/primitives/tooltip'
import { produceWithAutoFreezeDisabled } from '@shared/utils'
import { type Node, useUpdateNodeInternals } from '@xyflow/react'
import { ChevronRight, Lock } from 'lucide-react'
import {
  LOCKED_TOOLTIP,
  openBuddyEnterpriseRequest,
} from 'v2/shared/utils/buddy-enterprise-request'
import { FlowSourceTypes, FlowTypes } from 'v2/types'
import { getDefaultStartBaseNode } from '../config/nodes.config'
import { hasFormFields } from '../sidebar/start'
import { useFlowStore } from '../store'
import { FlowNodeTypes, type NodeDataBase, type StartNodeData } from '../types'
import { getChannelTypeFromFlowType, getTriggerTypeFromPhloType, refreshNodeVars } from '../utils'

interface TriggerItemProps {
  title: string
  description: string
  icon: React.ReactNode
  type: FlowTypes
  isExternalApp?: boolean
  locked?: boolean
  onClick?: () => void
}

export const TriggerItem = ({
  title,
  description,
  icon,
  type,
  isExternalApp,
  locked,
  onClick,
}: TriggerItemProps) => {
  const setShowTriggerDialog = useFlowStore((state) => state.setShowTriggerDialog)
  const flowInstance = useFlowStore((state) => state.flowInstance)
  const setCurrentFlowType = useFlowStore((state) => state.setCurrentFlowType)
  const updateNodeInternals = useUpdateNodeInternals()
  const setAgentMeta = useFlowStore((state) => state.setAgentMeta)
  const selectNode = useFlowStore((state) => state.selectNode)
  const discardChanges = useFlowStore((state) => state.discardChanges)

  const handleClick = () => {
    if (locked) {
      // Close the dialog first so the Buddy assistant panel is visible.
      setShowTriggerDialog(false)
      openBuddyEnterpriseRequest({ source: 'messaging' })
      return
    }

    if (isExternalApp && onClick) {
      onClick()
      return
    }

    // Get the current flow type before changing it
    const currentFlowType = useFlowStore.getState().currentFlowType

    // Clear any existing selection and unsaved changes before switching triggers
    discardChanges()

    // Update the flow type in the store
    setCurrentFlowType(type)

    // Find both the trigger and start nodes from the flow
    // We need both since changing trigger type affects both nodes
    const [triggerNode, startNode] = (flowInstance?.getNodes() || []).reduce<
      [Node<NodeDataBase<unknown>> | undefined, Node<NodeDataBase<unknown>> | undefined]
    >(
      (acc, node) => {
        if (node.type === FlowNodeTypes.select_trigger) acc[0] = node
        else if (node.type === FlowNodeTypes.start) acc[1] = node
        return acc
      },
      [undefined, undefined],
    )

    // Update agentMeta whenever we change the flow type
    // This ensures new AI nodes get the correct default channel
    if (currentFlowType !== type) {
      const newDefaultChannel = getChannelTypeFromFlowType(type)
      setAgentMeta({
        defaultChannel: newDefaultChannel,
      })
    }

    // Find the edge connected to start node to update its source handle
    const startNodeEdge = flowInstance?.getEdges().find((edge) => edge.source === startNode?.id)

    // Update the edge's source handle to match new trigger type
    if (startNodeEdge) {
      flowInstance?.updateEdge(startNodeEdge.id, {
        sourceHandle: FlowSourceTypes[type],
      })
      if (startNode) updateNodeInternals(startNode?.id)
    }

    // Reset trigger node to default state while preserving position
    if (triggerNode) {
      const newNodeState = getDefaultStartBaseNode({ triggerNode, phloType: type })
      flowInstance?.updateNode(triggerNode.id, newNodeState)
      updateNodeInternals(triggerNode?.id)
    } else if (startNode) {
      // If start node is there, update its trigger type
      flowInstance?.updateNode(
        startNode.id,
        produceWithAutoFreezeDisabled(
          startNode as Node<StartNodeData>,
          (draftNode: Node<StartNodeData>) => {
            if (draftNode.data.config) {
              const triggerValue = getTriggerTypeFromPhloType(type)
              // eslint-disable-next-line no-param-reassign
              draftNode.data.config.triggers = triggerValue ? [triggerValue] : []
            }
            return undefined
          },
        ),
      )
      updateNodeInternals(startNode?.id)
    }

    // Get the node that should be selected (either the transformed trigger node or existing start node)
    const nodeToSelect = triggerNode || startNode

    // Since the start node type is changed, refresh node vars for the start node,
    if (nodeToSelect) {
      refreshNodeVars(nodeToSelect?.id)
    }
    // Close the trigger dialog after selection
    setShowTriggerDialog(false)

    // Show confirmation modal for trigger changes (skip for API requests)
    if (
      currentFlowType &&
      currentFlowType !== 'omni' &&
      currentFlowType !== type &&
      type !== FlowTypes.api_request &&
      currentFlowType !== FlowTypes.api_request
    ) {
      useFlowStore.getState().setShowTriggerChangeConfirmationModal(true, {
        oldTriggerType: currentFlowType,
        newTriggerType: type,
      })
    }

    // Automatically select the start node to open its sidebar if it exists
    // Use setTimeout to ensure all node updates are processed first
    setTimeout(() => {
      if (nodeToSelect) {
        if (hasFormFields(type)) {
          selectNode(nodeToSelect.id)
        }
      }
    }, 100)
  }

  const item = (
    <Button
      className={cn(
        // A two-line list row (title + description), not a CTA: opt out of
        // Button's mono uppercase label type, which is unreadable at this
        // length. The builder-wide Inter override this used to sit alongside is
        // gone — every real button in the builder is mono again — so this
        // opt-out now stands on its own.
        'h-auto px-3 py-2 group w-full font-inter normal-case tracking-normal',
        isExternalApp ? 'justify-between' : 'justify-start',
        locked && 'opacity-50',
      )}
      data-testid={`trigger-item-${type}`}
      onClick={handleClick}
      variant="ghost"
    >
      <div
        className={cn('flex gap-3 flex-1 min-w-0', isExternalApp ? 'items-center' : 'items-start')}
      >
        {/* h-5 = the title's line-height, so the glyph centres on that line */}
        <span className="flex h-5 flex-shrink-0 items-center">{icon}</span>
        <div className="flex flex-col gap-1 flex-1 min-w-0 text-left">
          <span className="text-p-500 truncate flex items-center gap-1.5">
            {title}
            {locked && <Lock className="h-3.5 w-3.5 flex-shrink-0" strokeWidth={2.5} />}
          </span>
          {!isExternalApp && (
            <span className="text-xs-400 text-tertiary break-words whitespace-normal overflow-hidden leading-[18px]">
              {description}
            </span>
          )}
        </div>
      </div>
      {isExternalApp && (
        <ChevronRight
          size={16}
          className="text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        />
      )}
    </Button>
  )

  if (locked) {
    return (
      <Tooltip content={LOCKED_TOOLTIP} delayDuration={300}>
        {item}
      </Tooltip>
    )
  }

  return item
}
